/**
 * @fileoverview Export the filtered lesson list as a spreadsheet, with a
 * field picker.
 *
 * Two things it deliberately does NOT do. It does not offer its own period
 * control: the export is always exactly what the page is showing, so the row
 * count on the button and the row count in the file can never differ. And it
 * does not let you export nothing — the confirm is disabled with every field
 * unticked, rather than writing a file with a header and no columns.
 */

import { useEffect, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/studio/primitives";
import { EXPORT_FIELDS, periodLabel, presetExportFields } from "@/lib/lessonHistory";

function FieldRow({ field, checked, onToggle }) {
  return (
    <label
      className="flex cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2 transition-colors hover:bg-[var(--studio-overlay)]"
      data-testid={`export-field-${field.id}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 shrink-0 accent-[var(--accent-yellow)]"
      />
      <span className="font-sans text-[14px] text-[var(--text-secondary)]">{field.label}</span>
    </label>
  );
}

export default function LessonExportDialog({ open, onOpenChange, rowCount, period, onExport }) {
  const [selected, setSelected] = useState(presetExportFields);

  // Reopening resets to the preset. The picker is a per-export choice, not a
  // saved preference — remembering a one-off tweak means the next export is
  // silently shaped by something the instructor did weeks ago.
  useEffect(() => {
    if (open) setSelected(presetExportFields());
  }, [open]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));

  const ordered = EXPORT_FIELDS.map((f) => f.id).filter((id) => selected.includes(id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-[var(--studio-divider-strong)] bg-[var(--studio-raised)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-light text-[var(--text-primary)]">Export lessons</DialogTitle>
          <DialogDescription className="font-sans text-[13px] text-[var(--text-tertiary)]">
            {rowCount.toLocaleString()} {rowCount === 1 ? "lesson" : "lessons"} · {periodLabel(period)} · opens in Excel,
            Numbers or Google Sheets
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)]">Columns</span>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setSelected(EXPORT_FIELDS.map((f) => f.id))}
                className="font-sans text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="export-select-all"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setSelected(presetExportFields())}
                className="font-sans text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="export-reset"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="max-h-[46vh] overflow-y-auto rounded-[12px] border border-[var(--studio-divider)] p-1">
            {EXPORT_FIELDS.map((field) => (
              <FieldRow
                key={field.id}
                field={field}
                checked={selected.includes(field.id)}
                onToggle={() => toggle(field.id)}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="bare" type="button" onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            icon="download"
            disabled={!ordered.length}
            onPress={() => onExport(ordered)}
            data-testid="export-confirm"
          >
            Export {ordered.length} {ordered.length === 1 ? "column" : "columns"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
